import * as React from "react";
import type { JSX } from "react";

import { AjaxManager } from "@impero-com/common/utils/ajax";
import * as DV from "@impero-com/common/utils/dataView";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    ButtonGroup,
    ButtonSpec,
    emitNotification,
    SelectedTabParam,
    Tabs,
    TabSpec,
} from "@impero-com/ui";
import PQueue from "p-queue";

import { EntityView } from "api/generated/model";
import { getRisksRiskDirectoryIdNodes } from "api/generated/risks/risks";
import { ConditionalDisplay } from "components/conditionalDisplay";
import { Drawer } from "components/drawer";
import IconDelete from "img/delete.svg";
import * as R from "org/risks/types";
import { UpdateRootNodeLinkedResourcesCallback } from "org/risks/widgets/attachedFilesField";

import * as T from "../types";
import { getId, getDirectoryChildren, getScope } from "../utils";

import { NodeDetailsTab } from "./detailsTab";
import { DeleteNodeDialog, DeleteNodeDialogType } from "./dialog";
import { NodeEntitiesTab } from "./entitiesTab";
import * as theme from "./theme.css";

class DirectoryNodeDrawer extends React.PureComponent<
    DirectoryNodeDrawerProps,
    DirectoryNodeDrawerState
> {
    private queue: PQueue = new PQueue({ concurrency: 1 });

    constructor(props: DirectoryNodeDrawerProps) {
        super(props);
        this.state = {
            currentTabId:
                props.nodeDescriptor.value.type !== "RiskDirectory"
                    ? NODE_DETAILS_DRAWER_TAB
                    : ENTITIES_DRAWER_TAB,
            deleteDialogType: undefined,
        };
    }

    componentWillUnmount = () => {
        // Clear the queue. However, the action in progress cannot cancelled.
        // The action in progress is likely to update a node in the parent view after
        // this component is unmounted. That is life.
        this.queue.clear();
    };

    render = () => {
        const {
            riskDirectoryView,
            nodeDescriptor,
            ancestorsEntities,
            nodeEntities,
            descendantsEntities,
            entities,
            metadataExtractors,
            canEditNode,
            canDeleteEntityLink,
            canDeleteNode,
            onClose,
            updateRootNodeLinkedResources,
        } = this.props;
        const { currentTabId } = this.state;
        const tabs: Array<TabSpec> = [];
        const node = nodeDescriptor.value;
        switch (node.type) {
            case "RiskDirectory":
                break;
            case "Process":
                tabs.push({
                    id: NODE_DETAILS_DRAWER_TAB,
                    label: t("RiskDirectory_NodeUpdate_ProcessDetails"),
                    onClick: this.handleDrawerTabClick,
                });
                break;
            case "Risk":
                tabs.push({
                    id: NODE_DETAILS_DRAWER_TAB,
                    label: t("RiskDirectory_NodeUpdate_RiskDetails"),
                    onClick: this.handleDrawerTabClick,
                });
                break;
            default:
                return assertNever(node);
        }
        if (getScope(node).scope === "Global") {
            tabs.push({
                id: ENTITIES_DRAWER_TAB,
                label: `${t("RiskMapResponses_RiskMapEntities")} (${
                    Object.keys(ancestorsEntities).length + nodeEntities.length
                })`,
                onClick: this.handleDrawerTabClick,
            });
        }
        // Guard against incorrect code (e.g. incorrect default tab)
        if (!(tabs.findIndex((tab) => tab.id === currentTabId) >= 0)) {
            throw new Error("Unexpected tab ID " + currentTabId);
        }

        // NOTE: The 2 tabs content are rendered in the DOM and CONDITIONALLY DISPLAYED.
        // The rationale is that the FileUploader should NOT be unmounted, otherwise the upload
        // of potential files could be canceled. Conditionally displaying instead of conditionally rendering
        // makes sure that switching between tabs in the drawer wont accidentally cancel the upload.
        let nodeDetailsTab: JSX.Element = <></>;
        switch (node.type) {
            case "RiskDirectory":
                break;
            case "Risk":
            case "Process":
                nodeDetailsTab = (
                    <NodeDetailsTab
                        riskDirectoryView={riskDirectoryView}
                        nodeDescriptor={nodeDescriptor}
                        canEditNode={canEditNode}
                        entities={entities}
                        metadataExtractors={metadataExtractors}
                        onQueueAsyncNodeUpdate={this.handleQueueAsyncNodeUpdate}
                        updateRootNodeLinkedResources={
                            updateRootNodeLinkedResources
                        }
                    />
                );
                break;
            default:
                assertNever(node);
        }
        const entitiesDrawerTab = (
            <NodeEntitiesTab
                riskDirectoryView={riskDirectoryView}
                node={node}
                ancestorsEntities={ancestorsEntities}
                nodeEntities={nodeEntities}
                descendantsEntities={descendantsEntities}
                entities={entities}
                canDeleteEntityLink={canDeleteEntityLink}
                onQueueAsyncNodeUpdate={this.handleQueueAsyncNodeUpdate}
            />
        );

        const buttons: ButtonSpec[] = [];
        if (currentTabId === NODE_DETAILS_DRAWER_TAB && canDeleteNode) {
            buttons.push({
                id: "drawer-delete-id",
                label: t("RiskDirectory_NodeUpdate_Delete"),
                type: "link",
                onClick: this.deleteNode,
                icon: {
                    svg: IconDelete,
                },
            });
        }

        return (
            <Drawer
                buildHeader={() => (
                    <Tabs
                        className={theme.drawerTabs}
                        tabSpecs={tabs}
                        currentTabId={currentTabId}
                        getPopupContainer={(parentElement) => parentElement}
                    />
                )}
                buildFooter={
                    buttons.length
                        ? () => <ButtonGroup buttonSpecs={buttons} />
                        : undefined
                }
                onCancel={() => {
                    if (this.queue.size === 0 && this.queue.pending === 0) {
                        onClose();
                    } else {
                        emitNotification(
                            "warning",
                            t("RiskDirectory_NodeUpdate_SavingYourData")
                        );
                    }
                }}
            >
                <ConditionalDisplay
                    displayed={
                        this.state.currentTabId === NODE_DETAILS_DRAWER_TAB
                    }
                >
                    {nodeDetailsTab}
                </ConditionalDisplay>
                <ConditionalDisplay
                    displayed={this.state.currentTabId === ENTITIES_DRAWER_TAB}
                >
                    {entitiesDrawerTab}
                </ConditionalDisplay>
                {this.deleteDialog()}
            </Drawer>
        );
    };

    private handleDrawerTabClick = (newSelectedTab: SelectedTabParam) => {
        this.setState({
            currentTabId: newSelectedTab.key as DrawerTabId,
        });
    };

    /*
     * Pops the appropriate dialog up so that the user confirms
     * the deletion of this node
     */
    private deleteNode = () => {
        this.queue.clear();
        const { riskDirectoryView, nodeDescriptor } = this.props;
        getRisksRiskDirectoryIdNodes(riskDirectoryView.content.resource.id, {
            only_with_linked_data: true,
        }).then((response) => {
            const dataSource: DV.DataSource<
                DV.NodeDescriptor<T.DirectoryNode>
            > = new DV.MultiTreeDataSource<T.DirectoryNode>(
                [response.data.content.resource.rootNode],
                getDirectoryChildren,
                getId,
                (node) => node.content.name
            );
            const dataNode = DV.find(
                dataSource.iter(),
                (desc) => desc.value.id === nodeDescriptor.value.id
            );
            const deleteDialogType: DeleteNodeDialogType =
                dataNode === undefined
                    ? { type: "OK_CANCEL" }
                    : { type: "WITH_CONFIRM_DIALOG" };
            this.setState({ deleteDialogType });
        });
    };

    private handleQueueAsyncNodeUpdate = (
        promiseAction: Promise<T.NodeUpdateAction<T.DirectoryNode>>
    ): Promise<void> => {
        return this.queue.add(async () => {
            // XXX DO NOT RESOLVE THIS PROPS BEFORE THE ACTION IS CALLED
            // Otherwise you will work on stale data.
            const { nodeDescriptor, updateNode } = this.props;

            const action = await promiseAction;
            await updateNode(action, nodeDescriptor.value);
        });
    };

    /*
     * Renders the appropriate dialog for the user to confirm deletion of a risk.
     * onClose is the callback that is getting called when the drawer is closed.
     */
    private deleteDialog(): React.ReactNode {
        const {
            ajaxManager,
            nodeDescriptor,
            riskDirectoryView,
            onDeleteNode,
            onClose,
        } = this.props;
        const { deleteDialogType } = this.state;
        const onCancel = () => {
            this.setState({ deleteDialogType: undefined });
        };
        const onDelete = (node: T.DirectoryNode) => {
            onCancel();
            onClose();
            onDeleteNode(node);
        };

        return deleteDialogType ? (
            <DeleteNodeDialog
                dialogType={deleteDialogType}
                ajaxManager={ajaxManager}
                node={nodeDescriptor.value}
                riskDirectoryView={riskDirectoryView}
                onDelete={onDelete}
                onCancel={onCancel}
            />
        ) : null;
    }
}

const NODE_DETAILS_DRAWER_TAB = "node-details-tab";
const ENTITIES_DRAWER_TAB = "entities-tab";

type DrawerTabId = typeof NODE_DETAILS_DRAWER_TAB | typeof ENTITIES_DRAWER_TAB;

interface DirectoryNodeDrawerProps {
    ajaxManager: AjaxManager;
    riskDirectoryView: R.RiskDirectoryView;
    nodeDescriptor: DV.NodeDescriptor<T.DirectoryNode>;
    ancestorsEntities: Record<number, string>;
    nodeEntities: Array<number>;
    metadataExtractors: Array<
        DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>
    >;
    descendantsEntities: Record<number, string[]>;
    entities: Record<number, EntityView>;
    canEditNode: boolean;
    canDeleteEntityLink: boolean;
    canDeleteNode: boolean;
    updateNode: (
        action: T.NodeUpdateAction<T.DirectoryNode>,
        targetNode: T.DirectoryNode
    ) => Promise<void>;
    onDeleteNode: (node: T.DirectoryNode) => void;
    onClose: () => void;
    updateRootNodeLinkedResources: UpdateRootNodeLinkedResourcesCallback;
}

interface DirectoryNodeDrawerState {
    currentTabId: DrawerTabId;
    deleteDialogType: DeleteNodeDialogType | undefined;
}

export { DirectoryNodeDrawer, type DirectoryNodeDrawerProps };
